// apps/admin/src/lib/zone-alignment.ts
//
// P5 UI Polish W5 T8 — Pure zone alignment/distribution helpers (spec §2.4).
//
// No React or store imports — these are purely functional transformations
// of Zone arrays. All returned positions are Math.round'd to keep values
// in-range for the Zone schema (z.number().int()).

import type { Zone } from '@displaycms/contracts';

/**
 * Align all zones to the leftmost (minimum) x edge.
 */
export function alignLeft(zones: Zone[]): Zone[] {
  if (zones.length === 0) return zones;
  const minX = Math.min(...zones.map((z) => z.x));
  return zones.map((z) => ({ ...z, x: minX }));
}

/**
 * Align all zones to the horizontal center of the anchor zone.
 * anchorIdx defaults to 0 (the first zone in the input array).
 */
export function alignHCenter(zones: Zone[], anchorIdx: number = 0): Zone[] {
  if (zones.length === 0) return zones;
  const anchor = zones[anchorIdx];
  if (anchor === undefined) return zones;
  const anchorCenterX = anchor.x + anchor.w / 2;
  return zones.map((z) => ({ ...z, x: Math.round(anchorCenterX - z.w / 2) }));
}

/**
 * Align all zones to the rightmost (maximum) right edge (x + w).
 */
export function alignRight(zones: Zone[]): Zone[] {
  if (zones.length === 0) return zones;
  const maxRight = Math.max(...zones.map((z) => z.x + z.w));
  return zones.map((z) => ({ ...z, x: maxRight - z.w }));
}

/**
 * Align all zones to the topmost (minimum) y edge.
 */
export function alignTop(zones: Zone[]): Zone[] {
  if (zones.length === 0) return zones;
  const minY = Math.min(...zones.map((z) => z.y));
  return zones.map((z) => ({ ...z, y: minY }));
}

/**
 * Align all zones to the vertical center of the anchor zone.
 * anchorIdx defaults to 0 (the first zone in the input array).
 */
export function alignVCenter(zones: Zone[], anchorIdx: number = 0): Zone[] {
  if (zones.length === 0) return zones;
  const anchor = zones[anchorIdx];
  if (anchor === undefined) return zones;
  const anchorCenterY = anchor.y + anchor.h / 2;
  return zones.map((z) => ({ ...z, y: Math.round(anchorCenterY - z.h / 2) }));
}

/**
 * Align all zones to the bottommost (maximum) bottom edge (y + h).
 */
export function alignBottom(zones: Zone[]): Zone[] {
  if (zones.length === 0) return zones;
  const maxBottom = Math.max(...zones.map((z) => z.y + z.h));
  return zones.map((z) => ({ ...z, y: maxBottom - z.h }));
}

/**
 * Distribute zones with equal horizontal spacing between their centers.
 * Sorts by centerX; first and last zones stay fixed. Requires ≥3 zones.
 */
export function distributeH(zones: Zone[]): Zone[] {
  if (zones.length < 3) return zones;
  const sorted = [...zones].sort((a, b) => a.x + a.w / 2 - (b.x + b.w / 2));
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  if (first === undefined || last === undefined) return zones;
  const firstCenterX = first.x + first.w / 2;
  const lastCenterX = last.x + last.w / 2;
  const step = (lastCenterX - firstCenterX) / (sorted.length - 1);
  return sorted.map((z, i) => ({
    ...z,
    x: Math.round(firstCenterX + i * step - z.w / 2),
  }));
}

/**
 * Distribute zones with equal vertical spacing between their centers.
 * Sorts by centerY; first and last zones stay fixed. Requires ≥3 zones.
 */
export function distributeV(zones: Zone[]): Zone[] {
  if (zones.length < 3) return zones;
  const sorted = [...zones].sort((a, b) => a.y + a.h / 2 - (b.y + b.h / 2));
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  if (first === undefined || last === undefined) return zones;
  const firstCenterY = first.y + first.h / 2;
  const lastCenterY = last.y + last.h / 2;
  const step = (lastCenterY - firstCenterY) / (sorted.length - 1);
  return sorted.map((z, i) => ({
    ...z,
    y: Math.round(firstCenterY + i * step - z.h / 2),
  }));
}
